/**
 * M8 — slalom proportion. The TRACK is bigger than the world.
 *
 * The generator plans in TRACK CELLS (the abstract grid all the planning
 * math lives in). At render scale, one track cell spans TRACK_SCALE world
 * units: track placements are positioned ×2 and their meshes scaled ×2,
 * while the marble, island blocks, and decor keep world scale 1 — the
 * channel becomes a slalom ≈ 3.5 marble diameters wide, so a physics
 * marble (M10) has real room before it can fall off.
 *
 * ENDLESS-ONLY: the finite loop keeps 1:1 (its seeds/goldens survive).
 * One track cell maps to a 2×2 patch of world-grid island cells; island
 * masses and their decor are generated directly on the world grid.
 */
import type { CurveSeg, Vec3 } from "../../shared/types.ts";

export const TRACK_SCALE = 2;

export function scaleVec(v: Vec3): Vec3 {
  return [v[0] * TRACK_SCALE, v[1] * TRACK_SCALE, v[2] * TRACK_SCALE];
}

/** Uniformly scale a centerline segment into world units. */
export function scaleSeg(seg: CurveSeg): CurveSeg {
  switch (seg.kind) {
    case "line":
      return { kind: "line", a: scaleVec(seg.a), b: scaleVec(seg.b) };
    case "arc":
      return {
        kind: "arc",
        center: scaleVec(seg.center),
        radius: seg.radius * TRACK_SCALE,
        a0: seg.a0,
        a1: seg.a1,
        y0: seg.y0 * TRACK_SCALE,
        y1: seg.y1 * TRACK_SCALE,
      };
    case "cubic":
      return {
        kind: "cubic",
        p: [
          scaleVec(seg.p[0]),
          scaleVec(seg.p[1]),
          scaleVec(seg.p[2]),
          scaleVec(seg.p[3]),
        ],
      };
  }
}

/** The four world-grid cell centers covered by one track cell. */
export function worldCellsOf(
  cx: number,
  cz: number,
): readonly (readonly [number, number])[] {
  const wx = cx * TRACK_SCALE;
  const wz = cz * TRACK_SCALE;
  return [
    [wx - 0.5, wz - 0.5],
    [wx + 0.5, wz - 0.5],
    [wx - 0.5, wz + 0.5],
    [wx + 0.5, wz + 0.5],
  ];
}
